// eslint-disable-next-line simple-import-sort/imports
import { useNavigate, useParams } from "react-router-dom";
import ImageUploading from 'react-images-uploading';
import { v4 as uuidv4 } from 'uuid';
import "./index.css";
import BackArrowIcon from "@/shared-components/icons/components/public/BackArrowIcon";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import useCache from "@/hooks/useCache";
import config from "@/config";
import ItemsList from "./ItemsList";
import { useEffect, useRef, useState } from "react";
import Carousel from "./Carousel";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from 'immutability-helper';
import Text from "@/shared-components/text/components/Text";
import ArtworkImage from "@/shared-components/cards/components/ArtworkImage";
import fetchWrapper from "@/services/fetchWrapper.service";
import Loader from "@/shared-components/loader/Loader";
import CategoryItem from "./CategoryItem";
import InfiniteScroll from "@/shared-components/lists/components/InfiniteScroll";
import { AutoSizer, List, WindowScroller } from "react-virtualized";
import useQuery from "@/hooks/useQuery";
import UserService from "@/services/user.service";

function VirtualList({ items, onLoadMore, query, onClickItem }) {
  const listRef = useRef();

  return (
    <>
      <InfiniteScroll
        className="customize-search-type-gallery-virtual-list"
        onLoadMore={onLoadMore}
        shouldLoadMore={items.data.length < items.totalDocs}
        offsetElements={3}
        listLength={items.data.length}
      >
        {({ infiniteScrollAssigner, infiniteScrollParent }) =>
          !infiniteScrollParent ? (
            <></>
          ) : (
            <WindowScroller
              scrollElement={infiniteScrollParent ? infiniteScrollParent : window}
            >
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <AutoSizer disableHeight>
                  {({ width }) => (
                    <List
                      autoHeight
                      width={width ? width : 0.1}
                      height={height ? height : 0}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      rowCount={items.data.length}
                      rowHeight={33}
                      scrollTop={scrollTop}
                      overscanRowCount={5}
                      ref={listRef}
                      rowRenderer={({ index, key, style }) => (
                        <div key={key} style={style} ref={infiniteScrollAssigner(index)}>
                          <CategoryItem
                            displayText={items.data[index].user_name}
                            isSelected={query.some(
                              (q) => q === items.data[index].user_name
                            )}
                            onClickItem={() =>
                              onClickItem(items.data[index].user_name)
                            }
                          />
                        </div>
                      )}
                    />
                  )}
                </AutoSizer>
              )}
            </WindowScroller>
          )
        }
      </InfiniteScroll>
    </>
  );
}

export default function CustomizeSearchType() {
  const navigate = useNavigate();
  const params = useParams();
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [changesMade, setChangesMade] = useState(false);
  const [imageState, setImageState] = useState({});
  const [curUpload, setCurUpload] = useState(-1);
  const { loading, queryData, loadMoreData } = useQuery(
    "search_gallery",
    `${config.apis.api.url}/search/gallery`
  );

  const { type_id } = params;

  const { fetchData, refetch } = useCache(
    `search-type-${type_id}`,
    `${config.apis.api.url}/searchtype/${type_id}`,
    {
      type: "@cache/dynamic",
      injectToken: true,
    }
  );

  useEffect(() => {
    const core = async () => {
      if (type_id === "theme")
        setItems(config.search.main_search.artwork_filters.theme);
      if (type_id === "medium")
        setItems(config.search.main_search.artwork_filters.medium);
      if (type_id === "emotion")
        setItems(config.search.main_search.artwork_filters.emotion);
      if (type_id === "artist") {
        const res = await fetchWrapper.get(
          `${config.apis.api.url}/search/artist/both`
        );
        setItems(res.data.map((item) => item.artist_name || item.user_name));
      }
      if (type_id === "color")
        setItems(config.search.main_search.artwork_filters.color);
    };
    core();
  }, [type_id]);

  useEffect(() => {
    if (!fetchData) return;
    setSelectedItems([...fetchData.search_orders]);
  }, [fetchData]);

  const moveSelectedItem = (dragIndex, hoverIndex) => {
    setSelectedItems((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  };

  const onClickListItem = (item) => {
    const idx = selectedItems.findIndex((i) => i.title === item);
    const temp = [...selectedItems];
    if (idx >= 0) {
      temp.splice(idx, 1);
    } else {
      if (selectedItems.length > 3) {
        alert("Already 4 items are added!");
        return;
      }
      temp.push({ title: item, image: "9e1e080d-1c24-4c37-6d25-0d7251a69f00" });
    }
    setSelectedItems(temp);
  };

  const onSaveChanges = async () => {
    const res = await fetchWrapper.put(
      `${config.apis.api.url}/searchtypes/${type_id}/order`,
      {
        body: selectedItems,
        injectToken: true,
      }
    );
    if (res.data.message === "success") {
      refetch();
      setChangesMade(false);
      setCurUpload(-1);
    }
  };

  useEffect(() => {
    if (selectedItems.length !== 4) {
      setChangesMade(false);
      return;
    }
    if (curUpload >= 0) {
      setChangesMade(true);
      return;
    }

    let flag = false;
    selectedItems.forEach((item, idx) => {
      if (
        item.title !== fetchData.search_orders[idx].title ||
        item.image !== fetchData.search_orders[idx].image
      )
        flag = true;
    });
    if (flag) setChangesMade(true);
  }, [selectedItems, fetchData]);

  async function uploadImage(image) {
    const temporalImageId = uuidv4();

    setImageState({
      loading: true,
      temporalImageId,
      image_original_data: {
        name: image.name,
      },
    });

    const { response, data } = await UserService.uploadImage(image);

    if (response.ok) {
      setImageState((prevValue) => {
        if (Boolean(prevValue) && prevValue.temporalImageId === temporalImageId) {
          return data;
        } else {
          return prevValue;
        }
      });
      const temp = [...selectedItems];
      temp[curUpload].image = data.image_id;
      setSelectedItems(temp);
    } else {
      setImageState(null);

      alert(
        "Something went wrong. If the problem persists, contact us at contact@suarte.art."
      );
    }
  }

  function handlePictureError(errors) {
    if (errors.maxNumber) alert("You are only allowed to upload one image.");
    else if (errors.acceptType) alert("Invalid image type.");
    else if (errors.maxFileSize)
      alert(
        "File exceeds size limit (15 MB). Try uploading another."
      );
  }

  return (
    <div className="customize-search-type__container">
      <div className="customize-search-type__header">
        <div className="customize-search-type__header-back">
          <BackArrowIcon onClick={() => navigate(-1)} />
        </div>
        <h4 className="customize-search-type__header-text">
          {fetchData?.title}
        </h4>
        <div className="customize-search-type__header-logout">
          <ContinueButton turnOff={!changesMade} onClick={onSaveChanges}>
            Save changes
          </ContinueButton>
        </div>
      </div>
      <div className="customize-search-type__content">
        <Text className="customize-search-type__text">{fetchData?.title}</Text>
        <div className="customize-search-type__show-list">
          {selectedItems.map((item, idx) => (
            <div className="customize-search-type__type-container" key={idx}>
              <ImageUploading
                maxNumber={1}
                onChange={(imagesList) => uploadImage(imagesList[0].file)}
                onError={handlePictureError}
                maxFileSize={15000000}
              >
                {({ onImageUpload }) =>
                  curUpload === idx && imageState.loading ? (
                    <Loader thin />
                  ) : (
                    <ArtworkImage
                      image={item.image}
                      size={200}
                      loading
                      onClick={(e) => {
                        if (imageState.loading) return;
                        onImageUpload(e);
                        setCurUpload(idx);
                      }}
                      imageClassName="customize-search-type__type-image"
                    />
                  )
                }
              </ImageUploading>
              <Text>{item.title}</Text>
            </div>
          ))}
        </div>
        <Text className="customize-search-type__text">
          Select which themes will be showing
        </Text>
        <div className="customize-search-type__select-items">
          <div className="customize-search-type__order-items">
            <DndProvider backend={HTML5Backend}>
              {selectedItems.map((item, idx) => (
                <Carousel
                  key={item.title}
                  data={item}
                  index={idx}
                  id={item.title}
                  onClick={() => {}}
                  moveItem={moveSelectedItem}
                />
              ))}
            </DndProvider>
          </div>
          <div className="customize-search-type__items-list">
            {type_id === "gallery" ? (
              loading ? (
                <Loader className="search-filters-content-gallery__spinner" thin />
              ) : (
                <VirtualList
                  key={queryData.queryString}
                  items={queryData.data}
                  onLoadMore={loadMoreData}
                  query={selectedItems.map((item) => item.title)}
                  onClickItem={onClickListItem}
                />
              )
            ) : (
              <ItemsList
                selection={selectedItems.map((item) => item.title)}
                onClickItem={onClickListItem}
                items={items}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
